import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import moment from "moment";
import { ReactionBarSelector } from "@charkour/react-reactions";
import RepliesTo from "../../../sharedComponent/RepliesTo";
import MensionUsers from "../../../sharedComponent/MensionUsers";
import ChatMessage from "../../../sharedComponent/ChatMessage";
import ChatImage from "../../chatImage";
import { emojiReactions } from "../../../common";
import ApiConfig from "../../../api/ApiConfig";
import double_tick from "../../../assets/images/chat/double_tick.svg";
import gray_tick from "../../../assets/images/chat/gray_tick.svg";
import hr_three_dot from "../../../assets/images/hr_tree_dot.svg";
import fileIcon from "../../../assets/images/fileIcon.svg";
import downloadIcon from "../../../assets/images/ic_download.svg";
import { Button, Textarea, ImageCollage } from "../../../components/ui";
import ImageLightbox from "./ImageLightbox";
import { notifyError } from "../../../toaster/notify";

// Anchored extension match — we deliberately require the dot+ext to sit at the
// real end of the URL path (followed by `?`, `#`, end-of-string, or `/`) so a
// presigned S3 URL like `.../foo.jpg?signature=...` still resolves to "image"
// and a directory segment like `/png/` doesn't accidentally match. The
// extension list intentionally covers the long tail of formats we've seen
// real users paste/upload — Windows screenshots are often saved as `.jfif`,
// iOS shares are `.heic`/`.heif`, modern web exports use `.avif`.
const IMAGE_URL_RE =
  /\.(png|jpe?g|jfif|pjpeg|gif|webp|bmp|svg|heic|heif|avif|tiff?|ico)(\?|#|$|\/)/i;

// Some presigned URLs come through with an opaque path (no extension at all)
// but include the MIME type as a `response-content-type=image%2F...` query
// parameter. Trust that as a secondary signal so the lightbox burst doesn't
// skip those rows.
const IMAGE_MIME_QUERY_RE =
  /[?&](?:response-content-type|content-type)=image%2[Ff]/i;

const isImageUrl = (url) =>
  typeof url === "string" &&
  (IMAGE_URL_RE.test(url) || IMAGE_MIME_QUERY_RE.test(url));

// Extract a human filename + extension from a presigned/remote URL.
// Strips query/hash, takes the trailing path segment, decodes percent
// escapes, and surfaces the dotted extension as an UPPERCASE label so
// the file card can render a WhatsApp-style "PDF • 23 KB" detail line.
// Falls back to "Document" / "FILE" when the URL is opaque.
const getFileMeta = (url) => {
  if (!url || typeof url !== "string") {
    return { name: "Document", ext: "FILE" };
  }
  const cleanPath = url.split("?")[0].split("#")[0];
  let name = cleanPath.split("/").pop() || "Document";
  try {
    name = decodeURIComponent(name);
  } catch (_) {
    // leave the raw segment if decoding fails — better than throwing
  }
  const dotIdx = name.lastIndexOf(".");
  const ext = dotIdx >= 0 ? name.slice(dotIdx + 1).toUpperCase() : "FILE";
  return { name, ext };
};

// Date pill label for the centered divider — anchors recent messages
// against "Today" / "Yesterday" so users orient quickly without scanning timestamps.
const getDateLabel = (date) => {
  const m = moment(date);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return "Today";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  if (m.isSame(moment(), "year")) return m.format("MMM D");
  return m.format("MMM D, YYYY");
};

const MessageList = ({
  conversations,
  newMessages,
  userData,
  socket,
  currentSChannel,
  membersArray,
  editMsg,
  setEditMsg,
  replyTo,
  setReplyTo,
  selectedReaction,
  setselectedReaction,
  noMorePages,
  paginationLoader,
  page,
  setPage,
  firstMsg,
  lastMsg,
  msgRef,
  inputRef,
  addCreateTask,
  deletMessage,
  removeReaction,
}) => {
  // Tracks the most recently rendered date so we know when to drop a divider.
  // Reset on every render — list order is stable per render pass.
  let lastDateKey = null;

  // Lightbox state. Populated by the image-attachment click handlers below
  // with the list of image siblings from a single message and the index of
  // the clicked image, so the user can swipe between all images that were
  // sent together. `null` means closed.
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (images, startIndex, messages) => {
    if (!images || !images.length) return;
    setLightbox({
      images,
      messages: Array.isArray(messages) ? messages : [],
      startIndex: Math.max(0, startIndex || 0),
    });
  };
  const closeLightbox = () => setLightbox(null);

  // Per-image actions invoked from the lightbox toolbar. The bubble's ⋮
  // dropdown still acts on the whole album (Reply quotes the leader, Delete
  // cascades all members) — these handlers are the fine-grained counterpart,
  // available once the user has zoomed into a specific image.
  const handleLightboxReply = (message) => {
    if (!message) return;
    setReplyTo({ ...message });
    closeLightbox();
    if (inputRef && inputRef.current && inputRef.current.focus) {
      inputRef.current.focus();
    }
  };
  const handleLightboxDelete = (message, currentIndex) => {
    if (!message) return;
    const id = message.Team_Message_Id || message.message_id;
    if (id == null) return;
    deletMessage(id);
    // Splice the just-deleted image out of the open lightbox so the user can
    // immediately continue browsing the rest. Snap the index to whatever sits
    // at the same slot, or back off one if we just deleted the tail. Closing
    // entirely (return null) when the gallery empties.
    setLightbox((prev) => {
      if (!prev) return prev;
      const nextImages = prev.images.filter((_, idx) => idx !== currentIndex);
      const nextMessages = (prev.messages || []).filter(
        (_, idx) => idx !== currentIndex
      );
      if (nextImages.length === 0) return null;
      const nextStart = Math.min(currentIndex, nextImages.length - 1);
      return {
        ...prev,
        images: nextImages,
        messages: nextMessages,
        startIndex: nextStart,
      };
    });
  };

  // The chat backend emits a separate message-sent event per attached file, so
  // a "5 images at once" send-action arrives as 5 distinct messages, each
  // with one image. We group those back into a virtual gallery for the
  // lightbox.
  //
  // Window choice — 30 seconds:
  //   A "send-action" (one click of Send with N attachments) produces messages
  //   stamped within milliseconds of each other on the server. Two distinct
  //   send-actions, even back-to-back, are usually 5+ seconds apart. 30s
  //   gives generous slack for slow uploads / latency without bleeding into
  //   the *previous* send-action's images. A 5-minute window felt right for
  //   conversational adjacency (visually-grouped bubbles) but was wrong here
  //   — it pulled in 8 unrelated images from earlier batches.
  const BURST_WINDOW_SECONDS = 30;
  const messageId = (m) =>
    m && (m.Team_Message_Id != null ? m.Team_Message_Id : m.message_id);
  const messageSender = (m) => m && m.Sender_Id;
  const messageTime = (m) => m && (m.CreatedOn || m.created_at);
  const imagesInMessage = (m) => {
    if (!m) return [];
    if (Array.isArray(m.files) && m.files.length) {
      return m.files.filter(isImageUrl);
    }
    if (m.Resource_Path && isImageUrl(m.Resource_Path)) {
      return [m.Resource_Path];
    }
    return [];
  };

  const findImageBurst = (clickedMsg, clickedUrl) => {
    // Each entry pairs an image URL with the underlying message that owns
    // it, so the lightbox toolbar can act on a single image (Reply / Delete
    // for THIS image) rather than the whole album.
    const hereImgs = imagesInMessage(clickedMsg);
    const fallback = {
      images: [clickedUrl],
      messages: [clickedMsg],
      startIndex: 0,
    };
    if (!hereImgs.length) return fallback;
    const hereEntries = hereImgs.map((url) => ({ url, message: clickedMsg }));

    // Unified chronological order: history first, then new in-session messages.
    const all = [...(conversations || []), ...(newMessages || [])];
    const clickedId = messageId(clickedMsg);
    const clickedIdx = all.findIndex((m) => messageId(m) === clickedId);
    if (clickedIdx === -1) {
      // Fall back to per-message gallery if we can't locate the message in
      // either source array (defensive — shouldn't happen in normal flow).
      const startIndex = hereImgs.indexOf(clickedUrl);
      return {
        images: hereImgs,
        messages: hereEntries.map((e) => e.message),
        startIndex: startIndex >= 0 ? startIndex : 0,
      };
    }

    const clickedSender = messageSender(clickedMsg);
    // Loose-equality sender match. The rest of MessageList compares
    // Sender_Id with `==` (e.g. `prevMsg.Sender_Id == msg.Sender_Id` for the
    // bubble grouping rule), because the field arrives as a number from some
    // socket events and as a string from others. Strict equality here would
    // short-circuit the walk and reduce the burst to one image. A null/
    // undefined sender on a neighbor is treated as "unknown — keep going"
    // rather than a hard break, so a single sparse message can't bisect a
    // photo burst.
    const isSameSender = (a, b) => {
      if (a == null || b == null) return true;
      // eslint-disable-next-line eqeqeq
      return a == b;
    };

    // Walk backwards. Same-sender and time-window are HARD breaks. A neighbor
    // with no images is SKIPPED, not a break — a brief text reply ("nice!",
    // "check this out") between two image bursts shouldn't bisect the gallery
    // for the viewer.
    const beforeEntries = [];
    let prevTime = moment(messageTime(clickedMsg));
    for (let i = clickedIdx - 1; i >= 0; i--) {
      const m = all[i];
      if (!isSameSender(messageSender(m), clickedSender)) break;
      const t = moment(messageTime(m));
      if (
        t.isValid() &&
        prevTime.isValid() &&
        prevTime.diff(t, "seconds") > BURST_WINDOW_SECONDS
      )
        break;
      const imgs = imagesInMessage(m);
      if (imgs.length) {
        // Walk back-to-front through this message's images so the resulting
        // `beforeEntries` array stays in chronological forward order.
        for (let k = imgs.length - 1; k >= 0; k--) {
          beforeEntries.unshift({ url: imgs[k], message: m });
        }
      }
      if (t.isValid()) prevTime = t;
    }

    // Walk forwards.
    const afterEntries = [];
    prevTime = moment(messageTime(clickedMsg));
    for (let i = clickedIdx + 1; i < all.length; i++) {
      const m = all[i];
      if (!isSameSender(messageSender(m), clickedSender)) break;
      const t = moment(messageTime(m));
      if (
        t.isValid() &&
        prevTime.isValid() &&
        t.diff(prevTime, "seconds") > BURST_WINDOW_SECONDS
      )
        break;
      const imgs = imagesInMessage(m);
      if (imgs.length) {
        for (const url of imgs) {
          afterEntries.push({ url, message: m });
        }
      }
      if (t.isValid()) prevTime = t;
    }

    // Dedupe by URL while preserving encounter order. The same image can
    // legitimately appear twice in the unified walk in two ways:
    //   1. The chat backend echoes a sent message into both `newMessages`
    //      (live socket) and `conversations` (server-saved history) inside
    //      the same session, so the unified list contains both copies.
    //   2. The upload service may de-dup identical files server-side and
    //      return the same URL for multiple file slots in a burst.
    // Either way, the viewer should only show each unique image once. The
    // first-seen entry wins, so the message reference stored alongside is
    // the chronologically earliest owner — what we want for delete.
    const seenUrls = new Set();
    const galleryEntries = [];
    for (const entry of [...beforeEntries, ...hereEntries, ...afterEntries]) {
      if (seenUrls.has(entry.url)) continue;
      seenUrls.add(entry.url);
      galleryEntries.push(entry);
    }
    const images = galleryEntries.map((e) => e.url);
    const messages = galleryEntries.map((e) => e.message);
    const startIndex = images.indexOf(clickedUrl);
    return {
      images,
      messages,
      startIndex: startIndex >= 0 ? startIndex : 0,
    };
  };

  const openImageBurst = (clickedMsg, clickedUrl) => {
    const burst = findImageBurst(clickedMsg, clickedUrl);
    openLightbox(burst.images, burst.startIndex, burst.messages);
  };

  // ── Burst anchors for in-bubble collage rendering ──────────────────────
  //
  // Why this is separate from `findImageBurst`:
  //   * findImageBurst answers "what should the lightbox swipe through?" —
  //     it runs ONCE on click and walks the unified [conversations,
  //     newMessages] list.
  //   * computeBurstAnchors answers "what should each rendered row look
  //     like?" — it runs EVERY render and decides, per source array, which
  //     rows render as a leader (with a multi-image ImageCollage) vs. which
  //     get skipped (because the leader already covers them).
  //
  // The two solutions converge on the same grouping rule but apply at
  // different layers. The render-side rule is critical for the post-refresh
  // case the user reported: the server returns 4 separate conversation rows
  // for a 4-photo send, and without anchors we'd draw 4 bubbles. With
  // anchors, the first row renders an ImageCollage of all 4 URLs and the
  // other 3 rows return null.
  //
  // A "pure image" row is required for grouping — if a row carries text or
  // any non-image attachment, it stays solo so the user doesn't lose
  // sender context, captions, file cards, etc. when their bubble gets
  // folded into a sibling's collage.
  const computeBurstAnchors = (msgs) => {
    const anchors = {};
    if (!Array.isArray(msgs) || !msgs.length) return anchors;

    let burst = null;
    const flush = () => {
      if (!burst || burst.length < 2) {
        burst = null;
        return;
      }
      const leader = burst[0];
      anchors[leader.idx] = {
        role: "leader",
        // Concatenate every burst member's images. A single conversation
        // row only has one image (Resource_Path), but a newMessages row
        // can have several inside msg.files; keep both shapes flat.
        images: burst.flatMap((m) => m.images),
        memberIndices: burst.map((m) => m.idx),
        // Stable IDs needed by the Delete cascade so the action can fan out
        // to every server record represented by the visible collage. Falls
        // back gracefully across the conversations / newMessages id shapes.
        memberMessageIds: burst
          .map((m) => m.message.Team_Message_Id || m.message.message_id)
          .filter((id) => id != null),
      };
      for (let k = 1; k < burst.length; k++) {
        anchors[burst[k].idx] = { role: "skip" };
      }
      burst = null;
    };

    const hasText = (m) =>
      Boolean(
        (m.Team_Message_Text && String(m.Team_Message_Text).trim().length) ||
          (m.message && String(m.message).trim().length)
      );

    for (let i = 0; i < msgs.length; i++) {
      const msg = msgs[i];
      const imgs = imagesInMessage(msg);

      // Mixed-content rows (image + text caption, image + file card, etc.)
      // stay solo — bundling them into a neighbor's collage would silently
      // hide the caption and confuse the reader.
      const hasNonImage =
        Array.isArray(msg.files) &&
        msg.files.some((f) => !isImageUrl(f));
      const eligible = imgs.length > 0 && !hasText(msg) && !hasNonImage;
      if (!eligible) {
        flush();
        continue;
      }

      const member = { idx: i, message: msg, images: imgs };
      if (!burst) {
        burst = [member];
        continue;
      }

      const last = burst[burst.length - 1].message;
      // Loose-equality sender match (same reasoning as findImageBurst's
      // isSameSender) — Sender_Id arrives typed inconsistently across
      // socket events.
      // eslint-disable-next-line eqeqeq
      const sameSender = last.Sender_Id == msg.Sender_Id;
      const lastT = moment(messageTime(last));
      const thisT = moment(messageTime(msg));
      const gapSec =
        lastT.isValid() && thisT.isValid()
          ? Math.abs(thisT.diff(lastT, "seconds"))
          : 0;

      if (sameSender && gapSec <= BURST_WINDOW_SECONDS) {
        burst.push(member);
      } else {
        flush();
        burst = [member];
      }
    }
    flush();
    return anchors;
  };

  const conversationAnchors = useMemo(
    () => computeBurstAnchors(conversations),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversations]
  );
  const newMessagesAnchors = useMemo(
    () => computeBurstAnchors(newMessages),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [newMessages]
  );

  const renderDateDivider = (date) => {
    const m = moment(date);
    if (!m.isValid()) return null;
    const key = m.format("YYYY-MM-DD");
    if (key === lastDateKey) return null;
    lastDateKey = key;
    return (
      <div className="comm-message__date-divider" aria-hidden="true">
        <span className="comm-message__date-pill">{getDateLabel(date)}</span>
      </div>
    );
  };

  return (
    <div className="comm-chatbox__body">
      <div className="comm-message__pagination" ref={firstMsg}>
        {!noMorePages ? (
          <Button
            variant="primary"
            loading={paginationLoader}
            onClick={() => setPage(page + 1)}
          >
            Load More
          </Button>
        ) : (
          "No more Conversations found"
        )}
      </div>

      {conversations.map((msg, index) => {
        // Burst-folded rows produce no bubble of their own — the leader's
        // bubble already renders the collage that visually represents this
        // row's image. Returning null here is what makes a 4-row server-split
        // send read as a single 2×2 collage after a refresh.
        const convAnchor = conversationAnchors[index];
        if (convAnchor && convAnchor.role === "skip") return null;

        let Mentioned_To =
          msg.Mentioned_To &&
          msg.Mentioned_To.split(",").map((id) => Number(id));
        let mensioned = membersArray.filter(
          (member) =>
            Mentioned_To && Mentioned_To.includes(member.User_Id)
        );
        // Reply target may live in `conversations` (server-loaded history) or
        // in `newMessages` (just-sent in this session). Probe both so a reply
        // to a recent in-session message resolves correctly.
        let repliedTo =
          msg.ReplyTo != null
            ? conversations.find(
                (reply) => reply.Team_Message_Id == msg.ReplyTo
              ) ||
              newMessages.find(
                (reply) =>
                  reply.Team_Message_Id == msg.ReplyTo ||
                  reply.message_id == msg.ReplyTo
              )
            : null;
        let sernderName = msg.FirstName + " " + msg.LastName;
        const isSent = msg && msg.Sender_Id == userData.EmpId;
        const dateDivider = renderDateDivider(msg.CreatedOn);
        const isEditing =
          msg.Team_Message_Id == editMsg.Team_Message_Id;
        // Grouped = same sender as prior message, within a 5-minute window
        // on the same calendar day. A new date divider always breaks the group.
        const prevMsg = index > 0 ? conversations[index - 1] : null;
        const isGrouped =
          !!prevMsg &&
          !dateDivider &&
          prevMsg.Sender_Id == msg.Sender_Id &&
          Math.abs(
            moment(msg.CreatedOn).diff(moment(prevMsg.CreatedOn), "minutes")
          ) <= 5;

        return (
          <React.Fragment key={"conversation" + index}>
            {dateDivider}
            <div
              className={
                "comm-message " +
                (isSent ? "comm-message--sent" : "comm-message--received") +
                (isGrouped ? " comm-message--grouped" : "")
              }
              id={"msg" + msg.Team_Message_Id}
              ref={(elem) => {
                msgRef.current[index] = elem;
                // A burst leader covers its skipped members visually, so
                // route their indices to the leader's element. Without this,
                // a "scroll to replied message" that targets a member would
                // resolve to undefined and silently no-op.
                if (
                  convAnchor &&
                  convAnchor.role === "leader" &&
                  convAnchor.memberIndices
                ) {
                  convAnchor.memberIndices.forEach((i) => {
                    msgRef.current[i] = elem;
                  });
                }
              }}
            >
              {!isSent && (
                <div className="comm-message__avatar">
                  <ChatImage
                    name={msg?.FirstName}
                    lastName={msg?.LastName}
                    PhotoPath={
                      ApiConfig.BASE_URL_FOR_IMAGES + msg.PhotoPath
                    }
                  />
                </div>
              )}

              <div className="comm-message__column">
                {!isSent && (
                  <span className="comm-message__sender">{sernderName}</span>
                )}

                {msg.ReplyTo !== null && (
                  <div className="comm-message__reply-context">
                    <RepliesTo
                      sernderName={sernderName}
                      msg={msg}
                      repliedTo={repliedTo}
                      msgRef={msgRef}
                      users={mensioned}
                      index={index}
                    />
                  </div>
                )}

                {msg.Resource_Path != null &&
                  msg.Resource_Path.length > 0 && (
                    <div className="comm-message__attachments">
                      {convAnchor && convAnchor.role === "leader" ? (
                        // Leader of a server-split image burst — fold the
                        // collected URLs into one collage so the bubble
                        // matches what live (msg.files) multi-image messages
                        // already render as.
                        <ImageCollage
                          images={convAnchor.images}
                          onImageClick={(idx) =>
                            openImageBurst(msg, convAnchor.images[idx])
                          }
                          ariaLabel={`${convAnchor.images.length} photos`}
                        />
                      ) : /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i.test(
                          msg.Resource_Path
                        ) ? (
                        <div className="comm-message__attachment comm-message__attachment--video">
                          <video
                            src={msg.Resource_Path}
                            controls
                            preload="metadata"
                            playsInline
                            className="comm-message__video"
                          />
                        </div>
                      ) : msg.Resource_Path.includes("pdf") ||
                        msg.Resource_Path.includes("xls") ||
                        msg.Resource_Path.includes("xlsx") ||
                        msg.Resource_Path.includes("csv") ||
                        msg.Resource_Path.includes("doc") ||
                        msg.Resource_Path.includes("docx") ||
                        msg.Resource_Path.includes("zip") ? (
                        (() => {
                          const meta = getFileMeta(msg.Resource_Path);
                          return (
                            <a
                              href={msg.Resource_Path}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="comm-message__attachment comm-message__attachment--file"
                            >
                              <span
                                className="comm-message__file-icon"
                                data-ext={meta.ext}
                              >
                                <img src={fileIcon} alt="" aria-hidden="true" />
                              </span>
                              <span className="comm-message__file-info">
                                <span
                                  className="comm-message__file-name"
                                  title={meta.name}
                                >
                                  {meta.name}
                                </span>
                                <span className="comm-message__file-detail">
                                  {meta.ext}
                                </span>
                              </span>
                              <span
                                className="comm-message__file-download"
                                aria-hidden="true"
                              >
                                <img src={downloadIcon} alt="" />
                              </span>
                            </a>
                          );
                        })()
                      ) : msg.Resource_Path.includes("mp3") ||
                        msg.Resource_Path.includes("m4a") ? (
                        <a
                          href={msg.Resource_Path}
                          download
                          target="_blank"
                          rel="noreferrer"
                          className="comm-message__attachment"
                        >
                          <audio src={msg.Resource_Path} controls></audio>
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="comm-message__attachment comm-message__attachment--image"
                          onClick={() =>
                            openImageBurst(msg, msg.Resource_Path)
                          }
                        >
                          <img src={msg.Resource_Path} alt="" />
                        </button>
                      )}
                    </div>
                  )}

                {isEditing ? (
                  <Textarea
                    className="comm-message__edit-input"
                    value={
                      editMsg.Team_Message_Text || editMsg.message
                    }
                    onChange={(e) =>
                      setEditMsg({
                        ...editMsg,
                        message: e.target.value,
                        Team_Message_Text: e.target.value,
                      })
                    }
                    onKeyUp={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        const trimmed = e.target.value.trim();
                        if (!trimmed.length) {
                          notifyError("Message cannot be empty");
                          return;
                        }
                        if (socket)
                          socket.emit("edit-message", {
                            schannel_id: currentSChannel,
                            message_id:
                              editMsg.message_id ||
                              editMsg.Team_Message_Id,
                            message: trimmed,
                          });
                        setEditMsg({
                          message_id: null,
                          Sender_Id: null,
                        });
                      }
                    }}
                  />
                ) : (
                  msg?.Team_Message_Text && (
                    <div
                      className="comm-message__bubble-wrap"
                      onMouseEnter={() => {
                        setselectedReaction(msg.Team_Message_Id);
                      }}
                      onMouseLeave={() => setselectedReaction(null)}
                    >
                      <div
                        className={
                          "comm-message__bubble " +
                          (isSent
                            ? "comm-message__bubble--sent"
                            : "comm-message__bubble--received")
                        }
                      >
                        <div className="comm-message__text">
                          {msg.Team_Message_Text.includes("@") ? (
                            <MensionUsers
                              text={msg.Team_Message_Text}
                              users={mensioned}
                              index={index}
                            />
                          ) : !isSent &&
                            msg.Team_Message_Text.includes("<a") ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: msg.Team_Message_Text,
                              }}
                            />
                          ) : (
                            <ChatMessage msg={msg?.Team_Message_Text} />
                          )}
                        </div>
                      </div>

                      {selectedReaction &&
                        selectedReaction == msg.Team_Message_Id && (
                          <div className="comm-message__reaction-picker">
                            <ReactionBarSelector
                              iconSize={16}
                              reactions={emojiReactions}
                              onSelect={(selectedEmoji) => {
                                const selectedReaction = emojiReactions.find(
                                  (reaction) => reaction.label === selectedEmoji
                                );
                                const isSelectedEmojiPresent =
                                  msg?.reactions &&
                                  msg.reactions.length &&
                                  msg.reactions.some(
                                    (item) =>
                                      item.reaction == selectedReaction?.id
                                  );
                                if (!isSelectedEmojiPresent) {
                                  socket &&
                                    socket.emit("add-reaction", {
                                      reaction: selectedReaction?.id,
                                      schannel_id: msg?.Team_Channel_Id,
                                      message_id: msg?.Team_Message_Id,
                                    });
                                }
                              }}
                            />
                          </div>
                        )}
                    </div>
                  )
                )}

                <div className="comm-message__meta">
                  {isSent && (
                    <span className="comm-message__receipt">
                      <img
                        src={msg.IsAllRead ? double_tick : gray_tick}
                        alt="double_tick"
                      />
                    </span>
                  )}
                  <span className="comm-message__time">
                    {moment(msg.CreatedOn).fromNow()}
                  </span>
                  {msg.IsEdit && (
                    <span className="comm-message__edited">Edited</span>
                  )}
               {msg?.reactions &&
  msg.reactions.length &&
  msg.reactions.some((item) => {
    const displayEmoji = emojiReactions.find(
      (emj) => emj?.id == item?.reaction
    );
    return displayEmoji?.emoji;
  }) && (
    <span className="comm-message__reactions">
      {msg.reactions.map((item) => {
        let displayEmoji = emojiReactions.find(
          (emj) => emj?.id == item?.reaction
        );

        return displayEmoji?.emoji ? (
          <span
            key={item?.reaction_id}
            className="comm-message__reaction"
            onClick={() =>
              removeReaction(
                item?.reaction_id,
                msg?.Team_Channel_Id,
                msg?.Team_Message_Id
              )
            }
          >
            {displayEmoji.emoji}
          </span>
        ) : null;
      })}
    </span>
)}
                </div>
              </div>

              <Dropdown className="comm-message__actions ActionDropdown">
                <Dropdown.Toggle
                  variant="success"
                  id={"dropdown-basic" + index}
                  className="ActionToggle"
                >
                  <img src={hr_three_dot} alt="" />
                </Dropdown.Toggle>
                <Dropdown.Menu className="ActionMenu">
                  <ul className="actionnmenulist">
                    <li
                      onClick={() => {
                        setReplyTo({ ...msg });
                        inputRef.current.focus();
                      }}
                    >
                      Reply
                    </li>
                    {isSent &&
                      moment(msg.CreatedOn).fromNow() &&
                      (moment(msg.CreatedOn)
                        .fromNow()
                        .includes("seconds") ||
                        (moment(msg.CreatedOn)
                          .fromNow()
                          .includes("minutes") &&
                          Number(
                            moment(msg.CreatedOn)
                              .fromNow()
                              .split(" ")[0]
                          ) <= 15)) && (
                        <li
                          onClick={() => {
                            setEditMsg(msg);
                          }}
                        >
                          Edit
                        </li>
                      )}
                    {isSent && (
                      <li
                        onClick={() => {
                          // Folded image-burst bubbles represent multiple
                          // server records — fan the delete out so the whole
                          // album disappears in one go. Without this, only
                          // the leader's tile vanishes and the next render
                          // re-folds the remaining members into a smaller
                          // collage, which looks like a glitch.
                          const ids =
                            convAnchor &&
                            convAnchor.role === "leader" &&
                            convAnchor.memberMessageIds
                              ? convAnchor.memberMessageIds
                              : [msg.Team_Message_Id];
                          ids.forEach((id) => deletMessage(id));
                        }}
                      >
                        Delete
                      </li>
                    )}
                    {msg?.Team_Message_Text && (
                      <li
                        onClick={() => {
                          addCreateTask(
                            msg?.Team_Message_Text,
                            isSent
                              ? undefined
                              : msg?.Mentioned_To?.split(",")[0] || ""
                          );
                        }}
                      >
                        Create Task
                      </li>
                    )}
                  </ul>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </React.Fragment>
        );
      })}

      {newMessages &&
        newMessages.length > 0 &&
        newMessages.map((msg, index) => {
          // Same burst-anchor treatment as the conversations branch: skip
          // member rows so their leader can render the merged ImageCollage.
          // Important here because the chat backend emits one `message-sent`
          // event per attached file — without this, a live "send 4 photos"
          // would still flash 4 separate bubbles before the page is reloaded.
          const newAnchor = newMessagesAnchors[index];
          if (newAnchor && newAnchor.role === "skip") return null;

          let Mentioned_To =
            msg.Mentioned_To &&
            msg.Mentioned_To.split(",").map((id) => Number(id));
          let mensioned = membersArray.filter(
            (member) =>
              Mentioned_To && Mentioned_To.includes(member.User_Id)
          );
          // Match the reply target against both arrays. Previous fallback
          // compared `filtMsg.message_id == msg.message_id` — that resolved
          // to the message itself, causing nested in-session replies to
          // render their own text as the quote (duplicate above/below).
          let replied =
            msg.ReplyTo != null
              ? conversations.find(
                  (reply) => reply.Team_Message_Id == msg.ReplyTo
                ) ||
                newMessages.find(
                  (filtMsg) =>
                    filtMsg.Team_Message_Id == msg.ReplyTo ||
                    filtMsg.message_id == msg.ReplyTo
                )
              : null;
          let sender_name = msg.sender_name;
          const isSent = msg && msg.Sender_Id == userData.EmpId;
          const dateDivider = renderDateDivider(msg.created_at);
          const isEditing =
            msg.Sender_Id == editMsg.Sender_Id &&
            msg.message_id == editMsg.message_id;
          // Same grouping rule as conversations.map. For the first newMessages
          // item, fall back to the tail of conversations so a sender's last
          // saved message and first new message group across the boundary.
          const prevMsg =
            index > 0
              ? newMessages[index - 1]
              : conversations.length > 0
              ? conversations[conversations.length - 1]
              : null;
          const prevDate = prevMsg
            ? prevMsg.created_at || prevMsg.CreatedOn
            : null;
          const isGrouped =
            !!prevMsg &&
            !dateDivider &&
            prevMsg.Sender_Id == msg.Sender_Id &&
            Math.abs(
              moment(msg.created_at).diff(moment(prevDate), "minutes")
            ) <= 5;

          return (
            <React.Fragment key={"new Message" + index}>
              {dateDivider}
              <div
                className={
                  "comm-message " +
                  (isSent ? "comm-message--sent" : "comm-message--received") +
                  (isGrouped ? " comm-message--grouped" : "")
                }
                id={"msg" + msg.message_id}
                ref={(elem) => {
                  msgRef.current[index] = elem;
                  if (
                    newAnchor &&
                    newAnchor.role === "leader" &&
                    newAnchor.memberIndices
                  ) {
                    newAnchor.memberIndices.forEach((i) => {
                      msgRef.current[i] = elem;
                    });
                  }
                }}
                onBlur={() => setEditMsg({})}
              >
                {!isSent && (
                  <div className="comm-message__avatar">
                    <ChatImage
                      name={msg?.FirstName}
                      lastName={msg?.LastName}
                      PhotoPath={
                        ApiConfig.BASE_URL_FOR_IMAGES + msg.PhotoPath
                      }
                    />
                  </div>
                )}

                <div className="comm-message__column">
                  {!isSent && (
                    <span className="comm-message__sender">
                      {sender_name}
                    </span>
                  )}

                  {msg?.ReplyTo && (
                    <div className="comm-message__reply-context">
                      <RepliesTo
                        sernderName={isSent ? sender_name : msg?.sender_name}
                        msg={msg}
                        repliedTo={replied}
                        msgRef={msgRef}
                      />
                    </div>
                  )}

                  {msg.files && msg.files.length > 0 && (() => {
                    // Partition the file list once. Multi-image messages
                    // collapse into a single WhatsApp-style ImageCollage tile
                    // (the new UI primitive) so a "4 photos at once" send
                    // renders as a 2×2 grid instead of a stacked column. Mixed
                    // attachments (images + a video + a doc) still render
                    // their non-image members as individual cards underneath.
                    //
                    // When this row is a burst LEADER, expand `imageFiles` to
                    // include the burst members' images too — that's how a
                    // live server-split send (one message_id per image)
                    // collapses into one rendered collage.
                    const imageFiles =
                      newAnchor && newAnchor.role === "leader"
                        ? newAnchor.images
                        : msg.files.filter(isImageUrl);
                    const nonImageFiles = msg.files.filter(
                      (f) => !isImageUrl(f)
                    );
                    return (
                      <div className="comm-message__attachments">
                        {imageFiles.length >= 2 ? (
                          <ImageCollage
                            images={imageFiles}
                            onImageClick={(idx) =>
                              openImageBurst(msg, imageFiles[idx])
                            }
                            ariaLabel={`${imageFiles.length} photos`}
                          />
                        ) : imageFiles.length === 1 ? (
                          <button
                            type="button"
                            className="comm-message__attachment comm-message__attachment--image"
                            onClick={() =>
                              openImageBurst(msg, imageFiles[0])
                            }
                          >
                            <img src={imageFiles[0]} alt="" />
                          </button>
                        ) : null}
                        {nonImageFiles.map((file, fileIndex) => {
                          // Anchored extension match against the full URL so a
                          // `?signature=...` query doesn't break detection.
                          const isVideo =
                            typeof file === "string" &&
                            /\.(mp4|webm|mov|m4v|ogv|avi|mkv)(\?|#|$|\/)/i.test(
                              file
                            );
                          const isAudio =
                            typeof file === "string" &&
                            /\.(mp3|m4a|wav|ogg|aac)(\?|#|$|\/)/i.test(file);

                          if (isVideo) {
                            return (
                              <div
                                key={"video" + fileIndex}
                                className="comm-message__attachment comm-message__attachment--video"
                              >
                                <video
                                  src={file}
                                  controls
                                  preload="metadata"
                                  playsInline
                                  className="comm-message__video"
                                />
                              </div>
                            );
                          }
                          if (isAudio) {
                            return (
                              <a
                                key={"audio" + fileIndex}
                                href={file}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className="comm-message__attachment"
                              >
                                <audio src={file} controls></audio>
                              </a>
                            );
                          }
                          const meta = getFileMeta(file);
                          return (
                            <a
                              key={"file" + fileIndex}
                              href={file}
                              target="_blank"
                              rel="noreferrer"
                              className="comm-message__attachment comm-message__attachment--file"
                            >
                              <span
                                className="comm-message__file-icon"
                                data-ext={meta.ext}
                              >
                                <img src={fileIcon} alt="" aria-hidden="true" />
                              </span>
                              <span className="comm-message__file-info">
                                <span
                                  className="comm-message__file-name"
                                  title={meta.name}
                                >
                                  {meta.name}
                                </span>
                                <span className="comm-message__file-detail">
                                  {meta.ext}
                                </span>
                              </span>
                              <span
                                className="comm-message__file-download"
                                aria-hidden="true"
                              >
                                <img src={downloadIcon} alt="" />
                              </span>
                            </a>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {isEditing ? (
                    <Textarea
                      className="comm-message__edit-input"
                      value={
                        editMsg.Team_Message_Text || editMsg.message
                      }
                      onChange={(e) =>
                        setEditMsg({
                          ...editMsg,
                          message: e.target.value,
                          Team_Message_Text: e.target.value,
                        })
                      }
                      onKeyUp={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          const trimmed = e.target.value.trim();
                          if (!trimmed.length) {
                            notifyError("Message cannot be empty");
                            return;
                          }
                          if (socket)
                            socket.emit("edit-message", {
                              schannel_id: currentSChannel,
                              message_id:
                                editMsg.message_id ||
                                editMsg.Team_Message_Id,
                              message: trimmed,
                            });
                          setEditMsg({
                            message_id: null,
                            Sender_Id: null,
                          });
                        }
                      }}
                    />
                  ) : (
                    msg?.message && (
                      <div
                        className="comm-message__bubble-wrap"
                        onMouseEnter={() => {
                          setselectedReaction(msg?.message_id);
                        }}
                        onMouseLeave={() => setselectedReaction(null)}
                      >
                        <div
                          className={
                            "comm-message__bubble " +
                            (isSent
                              ? "comm-message__bubble--sent"
                              : "comm-message__bubble--received")
                          }
                        >
                          <div className="comm-message__text">
                            {msg.message.includes("@") ? (
                              <MensionUsers
                                text={msg.message}
                                users={mensioned}
                                index={index}
                              />
                            ) : !isSent && msg.message.includes("<a") ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: msg.message,
                                }}
                              />
                            ) : (
                              <ChatMessage msg={msg?.message} />
                            )}
                          </div>
                        </div>

                        {selectedReaction &&
                          selectedReaction == msg?.message_id && (
                            <div className="comm-message__reaction-picker">
                              <ReactionBarSelector
                                iconSize={16}
                                reactions={emojiReactions}
                                onSelect={(selectedEmoji) => {
                                  const selectedReaction = emojiReactions.find(
                                    (reaction) =>
                                      reaction.label === selectedEmoji
                                  );
                                  const isSelectedEmojiPresent =
                                    msg?.reactions &&
                                    msg.reactions.length &&
                                    msg.reactions.some(
                                      (item) =>
                                        item.reaction == selectedReaction?.id
                                    );
                                  if (!isSelectedEmojiPresent) {
                                    socket &&
                                      socket.emit("add-reaction", {
                                        reaction: selectedReaction?.id,
                                        schannel_id: msg?.schannel_id,
                                        message_id: msg?.message_id,
                                      });
                                  }
                                }}
                              />
                            </div>
                          )}
                      </div>
                    )
                  )}

                  <div className="comm-message__meta">
                    {isSent && (
                      <span className="comm-message__receipt">
                        <img
                          src={msg.IsAllRead ? double_tick : gray_tick}
                          alt="double_tick"
                        />
                      </span>
                    )}
                    <span className="comm-message__time">
                      {moment(msg.created_at).fromNow()}
                    </span>
                    {msg.IsEdit && (
                      <span className="comm-message__edited">Edited</span>
                    )}
                    {msg?.reactions && msg.reactions.length > 0 && (
                      <span className="comm-message__reactions">
                        {msg.reactions.map((item) => {
                          let displayEmoji = emojiReactions.find(
                            (emj) => emj?.id == item?.reaction
                          );
                          return (
                            <>
                            {displayEmoji?.emoji ? (
                            <span
                              className="comm-message__reaction"
                              onClick={() =>
                                removeReaction(
                                  item?.reaction_id,
                                  msg?.schannel_id,
                                  msg?.message_id
                                )
                              }
                            >
                              {displayEmoji?.emoji && displayEmoji?.emoji}
                            </span>
                            ) : null}
                            </>
                          );
                        })}
                      </span>
                    )}
                  </div>
                </div>

                <Dropdown className="comm-message__actions ActionDropdown">
                  <Dropdown.Toggle
                    variant="success"
                    id={"dropdown-basic" + index}
                    className="ActionToggle"
                  >
                    <img src={hr_three_dot} alt="" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="ActionMenu">
                    <ul className="actionnmenulist">
                      <li
                        onClick={() => {
                          setReplyTo({ ...msg });
                          inputRef.current.focus();
                        }}
                      >
                        Reply
                      </li>
                      {isSent && (
                        <li
                          onClick={() => {
                            setEditMsg(msg);
                          }}
                        >
                          Edit
                        </li>
                      )}
                      {isSent && (
                        <li
                          onClick={() => {
                            // Same album-cascade as the conversations branch
                            // — a folded leader represents N server records,
                            // so Delete must reach every one of them.
                            const ids =
                              newAnchor &&
                              newAnchor.role === "leader" &&
                              newAnchor.memberMessageIds
                                ? newAnchor.memberMessageIds
                                : [msg?.message_id];
                            ids
                              .filter((id) => id != null)
                              .forEach((id) => deletMessage(id));
                          }}
                        >
                          Delete
                        </li>
                      )}
                      {!isSent && <li>Create Task </li>}
                    </ul>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </React.Fragment>
          );
        })}

      <div ref={lastMsg}></div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          messages={lightbox.messages}
          startIndex={lightbox.startIndex}
          currentUserId={userData && userData.EmpId}
          onClose={closeLightbox}
          onReply={handleLightboxReply}
          onDelete={handleLightboxDelete}
        />
      )}
    </div>
  );
};

export default MessageList;
